import type { VulnerabilityOut } from '@/services/questionsService';
import { throwApiError } from '@/lib/errors';

const BACKEND_BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';

export const vulnerabilityService = {
  list: async (projectId: number): Promise<VulnerabilityOut[]> => {
    const response = await fetch(`${BACKEND_BASE}/api/vulnerabilities/${projectId}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to load vulnerabilities');
    }
    return response.json();
  },

  create: async (
    projectId: number,
    body: {
      name: string;
      vulnerability?: string;
      scenario?: string;
      countermeasure?: string;
      rating_vector_string?: string | null;
    },
  ): Promise<VulnerabilityOut> => {
    const response = await fetch(`${BACKEND_BASE}/api/vulnerabilities/${projectId}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to create vulnerability');
    }
    return response.json();
  },

  update: async (
    vulnerabilityId: number,
    body: {
      name?: string;
      vulnerability?: string;
      scenario?: string;
      countermeasure?: string;
      rating_vector_string?: string | null;
      status?: string;
      status_note?: string;
    },
    tcCredentials?: { cookie: string; projectVersionId: string },
  ): Promise<VulnerabilityOut> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (tcCredentials) {
      headers['X-ThreatCat-Cookie'] = tcCredentials.cookie;
      headers['X-ThreatCat-Project-Version-Id'] = tcCredentials.projectVersionId;
    }
    const response = await fetch(`${BACKEND_BASE}/api/vulnerabilities/${vulnerabilityId}`, {
      method: 'PUT',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to update vulnerability');
    }
    return response.json();
  },

  remove: async (vulnerabilityId: number): Promise<void> => {
    const response = await fetch(`${BACKEND_BASE}/api/vulnerabilities/${vulnerabilityId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to delete vulnerability');
    }
  },

  push: async (
    vulnerabilityId: number,
    cookie: string,
    projectVersionId: string,
  ): Promise<{ ok: boolean; threat: { id: number } }> => {
    const response = await fetch(`${BACKEND_BASE}/api/vulnerabilities/${vulnerabilityId}/push`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-ThreatCat-Cookie': cookie,
        'X-ThreatCat-Project-Version-Id': projectVersionId,
      },
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to push vulnerability to ThreatCat');
    }
    return response.json();
  },
};
